import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useApi } from "@/lib/auth";
import { fmtEventDateTime, formatPrice } from "@/lib/format";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Calendar, MapPin, User, ChevronRight, ChevronDown, ChevronUp, Clock } from "lucide-react";
import PageHeader from "@/components/PageHeader";

function bucket(items) {
  const now = Date.now();
  const upcoming = [], past = [];
  for (const e of items) {
    const t = e.starts_at ? new Date(e.starts_at).getTime() : NaN;
    if (Number.isFinite(t) && t < now) past.push(e);
    else upcoming.push(e);
  }
  upcoming.sort((a, b) => new Date(a.starts_at) - new Date(b.starts_at));
  past.sort((a, b) => new Date(b.starts_at) - new Date(a.starts_at));
  return { upcoming, past };
}

function EventCard({ ev }) {
  const isFree = ev.price_pence === 0;
  return (
    <Link
      to={`/events/${ev.event_id}`}
      data-testid={`event-card-${ev.event_id}`}
      className="block group"
    >
      <Card className="overflow-hidden border-border transition-shadow group-hover:shadow-md">
        <div className="flex items-center justify-between px-4 py-3 text-white gap-3"
             style={{ backgroundColor: `hsl(var(--mc-navy))` }}>
          <div className="text-sm font-semibold flex items-center gap-2 flex-1 min-w-0">
            <Calendar size={16} className="shrink-0" />
            <span className="truncate">{fmtEventDateTime(ev.starts_at) || "Date TBC"}</span>
          </div>
          <div
            className={[
              "text-xs font-semibold rounded-full px-3 py-1 shrink-0",
              isFree ? "bg-emerald-50 text-emerald-800" : "bg-white text-[hsl(var(--mc-navy))]",
            ].join(" ")}
            data-testid={`event-price-${ev.event_id}`}
          >
            {formatPrice(ev.price_pence, ev.currency)}
          </div>
        </div>
        <div className="p-4">
          <div className="flex items-start justify-between gap-3">
            <h3 className="text-lg font-semibold leading-snug">{ev.name}</h3>
            <ChevronRight className="text-muted-foreground shrink-0 mt-1" size={18} />
          </div>
          <div className="mt-2 flex items-center gap-4 text-sm text-muted-foreground">
            <span className="inline-flex items-center gap-1"><MapPin size={14} /> {ev.location || "Location TBA"}</span>
            <span className="inline-flex items-center gap-1"><User size={14} /> {ev.organiser || "Provincial team"}</span>
          </div>
          {typeof ev.places_remaining === "number" && ev.capacity_visible !== false && (
            <div className="mt-3">
              <Badge variant={ev.places_remaining < 5 ? "destructive" : "secondary"}>
                {ev.places_remaining} of {ev.capacity} left
              </Badge>
            </div>
          )}
        </div>
      </Card>
    </Link>
  );
}

function Section({ open, onToggle, title, icon: Icon, accent, items, empty, testId }) {
  return (
    <div className="bg-card border border-border rounded-lg overflow-hidden mb-4"
         style={{ borderLeft: `5px solid ${accent}` }}>
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-3 hover:bg-muted/40"
        data-testid={testId}
      >
        <div className="flex items-center gap-3">
          <Icon size={18} style={{ color: accent }} />
          <span className="text-sm font-semibold">{title}</span>
          <span className="text-xs font-semibold text-white rounded-full px-2 py-0.5"
                style={{ backgroundColor: accent }}>{items.length}</span>
        </div>
        {open ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </button>
      {open && (
        <div className="border-t border-border px-4 py-3">
          {items.length === 0 ? (
            <div className="text-sm text-muted-foreground italic py-2">{empty}</div>
          ) : (
            <div className="grid gap-3 md:grid-cols-2">
              {items.map((ev) => <EventCard key={ev.event_id} ev={ev} />)}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function EventsPage() {
  const api = useApi();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState({ upcoming: true, past: false });

  const load = async () => {
    try {
      const r = await api("/events");
      const data = await r.json();
      if (r.ok) setEvents(data.events || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  const buckets = useMemo(() => bucket(events), [events]);

  return (
    <div>
      <PageHeader
        eyebrow="Province of Middlesex"
        title="Upcoming events"
        subtitle="Browse and book from the roster of Provincial and Lodge events."
        testId="events-page-header"
      />
      {loading ? (
        <div className="text-sm text-muted-foreground">Loading events…</div>
      ) : events.length === 0 ? (
        <Card className="p-10 text-center">
          <Calendar className="mx-auto text-muted-foreground" size={32} />
          <h3 className="mt-3 font-semibold">No events scheduled</h3>
          <p className="text-sm text-muted-foreground mt-1">Check back soon.</p>
          <Button variant="outline" className="mt-4" onClick={load}>Refresh</Button>
        </Card>
      ) : (
        <>
          <Section
            open={expanded.upcoming}
            onToggle={() => setExpanded((e) => ({ ...e, upcoming: !e.upcoming }))}
            title="Upcoming events"
            icon={Calendar}
            accent="#112A46"
            items={buckets.upcoming}
            empty="No upcoming events scheduled."
            testId="events-section-upcoming"
          />
          <Section
            open={expanded.past}
            onToggle={() => setExpanded((e) => ({ ...e, past: !e.past }))}
            title="Past events"
            icon={Clock}
            accent="#4C5C74"
            items={buckets.past}
            empty="No past events to show yet."
            testId="events-section-past"
          />
        </>
      )}
    </div>
  );
}
